import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const sources = new Set();
const touched = new Map();
let lastClick = 0;
let bypass = false;
let ask = null;
let last = typeof window === "undefined" ? null : { url: window.location.href, state: window.history.state };
let popBypass = false;

const SKIP_TYPES = ["hidden", "search", "password", "button", "submit", "reset"];
const valueOf = (el) => (el.type === "checkbox" || el.type === "radio" ? String(el.checked) : el.type === "file" ? String(el.files?.length || 0) : el.value || "");
const trackable = (el) => el?.matches?.("input, textarea, select") && !SKIP_TYPES.includes(el.type) && !el.closest("header, nav, [data-guard-ignore]")
  && !/cerca|search|filtra/i.test(`${el.placeholder || ""} ${el.getAttribute("aria-label") || ""}`);

export const isDirty = () => {
  if (sources.size) return true;
  for (const [el, base] of touched) {
    if (!el.isConnected) { touched.delete(el); continue; }
    const v = valueOf(el);
    if (v !== base && v.trim() && v !== "0" && v !== "false") return true;
  }
  return false;
};

export const confirmLeave = () => (isDirty() && ask ? ask().then((ok) => { if (ok) touched.clear(); return ok; }) : Promise.resolve(true));

export function useLeaveGuard(active) {
  useEffect(() => {
    if (!active) return undefined;
    const token = {};
    sources.add(token);
    return () => { sources.delete(token); };
  }, [active]);
}

api.interceptors.response.use((r) => {
  if (["post", "put", "patch", "delete"].includes(r.config?.method) && Date.now() - lastClick < 4000) {
    for (const el of touched.keys()) touched.set(el, valueOf(el));
  }
  return r;
});

// Registered at import time so it runs before the router's own popstate listener.
const onPop = (e) => {
  if (popBypass) { popBypass = false; return; }
  if (!ask || !last || !isDirty()) return;
  e.stopImmediatePropagation();
  window.history.pushState(last.state, "", last.url);
  ask().then((ok) => {
    if (!ok) return;
    touched.clear();
    popBypass = true;
    window.history.back();
  });
};
if (typeof window !== "undefined") window.addEventListener("popstate", onPop, true);

const leavingTarget = (e) => {
  const el = e.target?.closest?.("a[href], [data-leave-guard]");
  if (!el) return null;
  if (el.matches("[data-leave-guard]")) return el;
  if (el.target === "_blank" || el.hasAttribute("download") || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return null;
  const url = new URL(el.href, window.location.href);
  if (url.origin !== window.location.origin) return null;
  return url.pathname === window.location.pathname && url.search === window.location.search ? null : el;
};

export const LeaveGuard = () => {
  const [open, setOpen] = useState(false);
  const resolver = useRef(null);
  const location = useLocation();
  useEffect(() => { last = { url: window.location.href, state: window.history.state }; }, [location]);
  useEffect(() => {
    ask = () => new Promise((res) => { resolver.current = res; setOpen(true); });
    const onFocus = (e) => { if (trackable(e.target) && !touched.has(e.target)) touched.set(e.target, valueOf(e.target)); };
    const onClick = (e) => {
      lastClick = Date.now();
      if (bypass) return;
      const el = leavingTarget(e);
      if (!el || !isDirty()) return;
      e.preventDefault();
      e.stopPropagation();
      ask().then((ok) => {
        if (!ok) return;
        touched.clear();
        bypass = true;
        try { el.click(); } finally { bypass = false; }
      });
    };
    const onUnload = (e) => { if (isDirty()) { e.preventDefault(); e.returnValue = ""; } };
    document.addEventListener("focusin", onFocus, true);
    document.addEventListener("click", onClick, true);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      ask = null;
      document.removeEventListener("focusin", onFocus, true);
      document.removeEventListener("click", onClick, true);
      window.removeEventListener("beforeunload", onUnload);
    };
  }, []);
  const done = (ok) => { setOpen(false); const r = resolver.current; resolver.current = null; r?.(ok); };
  return <AlertDialog open={open} onOpenChange={(o) => { if (!o) done(false); }}>
    <AlertDialogContent data-testid="leave-guard-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Vuoi uscire?</AlertDialogTitle>
        <AlertDialogDescription>Hai iniziato a compilare: se esci perderai i dati inseriti.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel onClick={() => done(false)} data-testid="leave-guard-stay">Resta qui</AlertDialogCancel>
        <AlertDialogAction onClick={() => done(true)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="leave-guard-leave">Esci e perdi i dati</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>;
};
